import React from 'react';
import {
  Box, Card, CardContent, Typography, Grid, Chip, ToggleButton, ToggleButtonGroup,
  Button, IconButton, Tooltip, Switch, FormControlLabel, TextField, MenuItem,
  Stack, Skeleton, Divider,
} from '@mui/material';
import {
  Refresh as RefreshIcon, Save as SaveIcon, Print as PrintIcon, FileDownload as DownloadIcon,
  Compare as CompareIcon, ArrowUpward as UpIcon, ArrowDownward as DownIcon,
} from '@mui/icons-material';
import { PERIOD_PRESETS, ReportFiltersState, formatPct } from './types';

interface KpiCardProps {
  title: string;
  value: React.ReactNode;
  subtitle?: React.ReactNode;
  icon?: React.ReactNode;
  color?: string;
  trendPct?: number | null;
  loading?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

export const KpiCard: React.FC<KpiCardProps> = ({ title, value, subtitle, icon, color = '#14b8a6', trendPct, loading, size = 'md' }) => {
  const isUp = (trendPct ?? 0) > 0;
  const isDown = (trendPct ?? 0) < 0;
  return (
    <Card sx={{ borderRadius: 3, height: '100%', border: `1px solid ${color}22`, transition: 'transform .15s, box-shadow .15s', '&:hover': { transform: 'translateY(-1px)', boxShadow: `0 4px 18px ${color}24` } }}>
      <CardContent sx={{ p: size === 'sm' ? 1.75 : 2.25, '&:last-child': { pb: size === 'sm' ? 1.75 : 2.25 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1, gap: 1 }}>
          <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase', lineHeight: 1.2 }}>
            {title}
          </Typography>
          {icon ? <Box sx={{ color, opacity: .9 }}>{icon}</Box> : null}
        </Box>
        {loading ? (
          <Skeleton width="80%" height={size === 'lg' ? 40 : 30} />
        ) : (
          <Typography variant={size === 'lg' ? 'h4' : 'h5'} sx={{ fontWeight: 700, color: '#0f172a', lineHeight: 1.2, wordBreak: 'break-word' }}>
            {value}
          </Typography>
        )}
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
          {subtitle ? <Typography variant="caption" sx={{ color: '#64748b' }}>{subtitle}</Typography> : null}
          {typeof trendPct === 'number' && Number.isFinite(trendPct) ? (
            <Chip
              size="small"
              icon={isUp ? <UpIcon sx={{ fontSize: 14 }} /> : isDown ? <DownIcon sx={{ fontSize: 14 }} /> : undefined}
              label={formatPct(trendPct)}
              sx={{
                height: 20,
                fontSize: '.7rem',
                fontWeight: 700,
                bgcolor: isUp ? '#dcfce7' : isDown ? '#fee2e2' : '#e2e8f0',
                color: isUp ? '#15803d' : isDown ? '#b91c1c' : '#475569',
              }}
            />
          ) : null}
        </Stack>
      </CardContent>
    </Card>
  );
};

interface ChartCardProps {
  title: string;
  subtitle?: string;
  height?: number;
  actions?: React.ReactNode;
  children: React.ReactNode;
}
export const ChartCard: React.FC<ChartCardProps> = ({ title, subtitle, height = 320, actions, children }) => (
  <Card sx={{ borderRadius: 3, height: '100%' }}>
    <CardContent sx={{ p: 2.25, '&:last-child': { pb: 2.25 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a' }}>{title}</Typography>
          {subtitle ? <Typography variant="caption" sx={{ color: '#64748b' }}>{subtitle}</Typography> : null}
        </Box>
        {actions}
      </Box>
      <Box sx={{ width: '100%', height }}>{children}</Box>
    </CardContent>
  </Card>
);

interface SectionCardProps {
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
}
export const SectionCard: React.FC<SectionCardProps> = ({ title, subtitle, actions, children }) => (
  <Card sx={{ borderRadius: 3 }}>
    <CardContent sx={{ p: 2.25, '&:last-child': { pb: 2.25 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a' }}>{title}</Typography>
          {subtitle ? <Typography variant="caption" sx={{ color: '#64748b' }}>{subtitle}</Typography> : null}
        </Box>
        {actions}
      </Box>
      <Divider sx={{ mb: 2 }} />
      {children}
    </CardContent>
  </Card>
);

interface FiltersBarProps {
  state: ReportFiltersState;
  onChange: (next: ReportFiltersState) => void;
  onRefresh: () => void;
  onSave?: () => void;
  onPrint?: () => void;
  onExport?: () => void;
  loading?: boolean;
  showCompare?: boolean;
  showUnitMode?: boolean;
  canSeeAllUnits?: boolean;
  /** admin / admin_master: filtrar relatórios financeiros por quem registrou o lançamento. */
  showRegistrarFilter?: boolean;
  registrarOptions?: { id: string; full_name?: string | null; email?: string | null }[];
}

export const FiltersBar: React.FC<FiltersBarProps> = ({ state, onChange, onRefresh, onSave, onPrint, onExport, loading, showCompare = true, showUnitMode = true, canSeeAllUnits, showRegistrarFilter, registrarOptions }) => {
  const handlePreset = (id: string) => {
    if (!id) return;
    const preset = PERIOD_PRESETS.find(p => p.id === id);
    if (!preset) return;
    const range = preset.resolve();
    onChange({ ...state, preset: id, from: range.from, to: range.to });
  };
  const handleDate = (key: 'from' | 'to', v: string) => onChange({ ...state, [key]: v, preset: 'custom' });

  return (
    <Card sx={{ borderRadius: 3, mb: 2 }}>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={12} md={6}>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={state.preset}
              onChange={(_, v) => handlePreset(v)}
              sx={{ flexWrap: 'wrap', gap: 0.5, '& .MuiToggleButton-root': { textTransform: 'none', borderRadius: '8px !important', border: '1px solid #e2e8f0 !important', px: 1.25, py: 0.5, fontSize: '.78rem', fontWeight: 600, color: '#475569', '&.Mui-selected': { bgcolor: '#0f766e', color: 'white', '&:hover': { bgcolor: '#0d9488' } } } }}
            >
              {PERIOD_PRESETS.map(p => (
                <ToggleButton key={p.id} value={p.id}>{p.label}</ToggleButton>
              ))}
              <ToggleButton value="custom" disabled>Personalizado</ToggleButton>
            </ToggleButtonGroup>
          </Grid>

          <Grid item xs={6} md={2}>
            <TextField fullWidth size="small" type="date" label="De" value={state.from} onChange={e => handleDate('from', e.target.value)} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField fullWidth size="small" type="date" label="Até" value={state.to} onChange={e => handleDate('to', e.target.value)} InputLabelProps={{ shrink: true }} />
          </Grid>
          {showUnitMode && canSeeAllUnits ? (
            <Grid item xs={12} md={2}>
              <TextField select fullWidth size="small" label="Unidade" value={state.unitMode} onChange={e => onChange({ ...state, unitMode: e.target.value as any })} >
                <MenuItem value="current">Atual</MenuItem>
                <MenuItem value="all">Todas</MenuItem>
              </TextField>
            </Grid>
          ) : null}

          {showRegistrarFilter ? (
            <Grid item xs={12} md={showUnitMode && canSeeAllUnits ? 12 : 4}>
              <TextField
                select
                fullWidth
                size="small"
                label="Caixa / usuário que registrou"
                value={state.registrarUserId || ''}
                onChange={e => onChange({ ...state, registrarUserId: e.target.value })}
                helperText="Somente para perfis administrativos. Filtra por quem lançou no financeiro."
              >
                <MenuItem value="">Todos os usuários da unidade</MenuItem>
                {(registrarOptions || []).map(u => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.full_name || u.email || u.id}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          ) : null}

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
              {showCompare ? (
                <FormControlLabel
                  control={<Switch size="small" checked={state.compare} onChange={(_, v) => onChange({ ...state, compare: v })} />}
                  label={<Typography variant="caption" sx={{ color: '#475569', fontWeight: 600 }}>Comparar com período anterior</Typography>}
                />
              ) : <Box />}
              <Stack direction="row" spacing={1}>
                <Tooltip title="Atualizar">
                  <span>
                    <IconButton size="small" onClick={onRefresh} disabled={loading} sx={{ color: '#0f766e' }}>
                      <RefreshIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                {onExport ? (
                  <Tooltip title="Exportar CSV">
                    <span>
                      <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={onExport} sx={{ textTransform: 'none', color: '#0f766e', borderColor: '#0f766e', '&:hover': { borderColor: '#0d9488', bgcolor: '#0f766e0a' } }}>
                        Exportar
                      </Button>
                    </span>
                  </Tooltip>
                ) : null}
                {onPrint ? (
                  <Tooltip title="Imprimir">
                    <span>
                      <Button size="small" variant="outlined" startIcon={<PrintIcon />} onClick={onPrint} sx={{ textTransform: 'none', color: '#0f766e', borderColor: '#0f766e', '&:hover': { borderColor: '#0d9488', bgcolor: '#0f766e0a' } }}>
                        Imprimir
                      </Button>
                    </span>
                  </Tooltip>
                ) : null}
                {onSave ? (
                  <Tooltip title="Salvar este relatório como documento">
                    <span>
                      <Button size="small" variant="contained" startIcon={<SaveIcon />} onClick={onSave} sx={{ textTransform: 'none', bgcolor: '#0f766e', '&:hover': { bgcolor: '#0d9488' } }}>
                        Salvar
                      </Button>
                    </span>
                  </Tooltip>
                ) : null}
              </Stack>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export const EmptyState: React.FC<{ children?: React.ReactNode; title?: string }> = ({ children, title = 'Sem dados no período' }) => (
  <Box sx={{ textAlign: 'center', py: 6, color: '#94a3b8' }}>
    <Typography variant="body2" sx={{ fontWeight: 600 }}>{title}</Typography>
    {children ? <Typography variant="caption">{children}</Typography> : null}
  </Box>
);

/** Converte um array de objetos em CSV (separador `;`) e dispara download. */
export function exportToCsv(rows: any[], filename: string, headers?: { key: string; label?: string }[]) {
  if (!rows || rows.length === 0) {
    alert('Nada para exportar');
    return;
  }
  const cols: { key: string; label?: string }[] = headers || Object.keys(rows[0]).map(k => ({ key: k }));
  const escape = (v: any) => {
    if (v === null || v === undefined) return '';
    const s = typeof v === 'object' ? JSON.stringify(v) : String(v);
    if (s.includes(';') || s.includes('"') || s.includes('\n')) {
      return '"' + s.replace(/"/g, '""') + '"';
    }
    return s;
  };
  const head = cols.map(c => escape(c.label || c.key)).join(';');
  const body = rows.map(r => cols.map(c => escape(r[c.key])).join(';')).join('\n');
  const csv = '\uFEFF' + head + '\n' + body; // BOM para Excel
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename + '.csv';
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 200);
}

/** Paleta usada nos charts. */
export const CHART_COLORS = ['#0f766e', '#14b8a6', '#22c55e', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444', '#06b6d4', '#a855f7', '#d946ef', '#84cc16', '#f43f5e'];
